import { Injectable } from '@angular/core';

import { VeevaAnnotation } from '@core/api/sources/types';

@Injectable({
  providedIn: 'root',
})
export class SimplePdfHighlightService {
  private highlightElements: HTMLElement[] = [];

  /**
   * Simple, direct approach to highlight PDF content
   */
  public highlightAnnotation(annotation: VeevaAnnotation): boolean {
    // Check if annotation can be highlighted
    if (
      !annotation.placemark ||
      !annotation.placemark.coordinates__sys ||
      annotation.placemark.coordinates__sys.length < 8
    ) {
      return false;
    }

    // Clear any existing highlights
    this.clearAllHighlights();

    try {
      // Give PDF viewer time to render
      setTimeout(() => {
        this.addTargetedHighlight(annotation);
      }, 500);

      return true;
    } catch (error) {
      return false;
    }
  }

  private addTargetedHighlight(annotation: VeevaAnnotation): void {
    const targetPageNumber = annotation.placemark?.page_number__sys || 1;

    // Find the specific page container
    const pageContainer = document.querySelector(
      `div.page[data-page-number="${targetPageNumber}"]`
    ) as HTMLElement;

    if (!pageContainer) {
      return;
    }

    // Create highlight overlay for this specific page
    this.createPageHighlight(annotation, pageContainer);
  }

  private createPageHighlight(annotation: VeevaAnnotation, pageContainer: HTMLElement): void {
    const coords = annotation.placemark?.coordinates__sys;
    if (!coords || coords.length < 8) {
      return;
    }

    // Extract bounding box from the 4 corner points
    const xCoords = [coords[0], coords[2], coords[4], coords[6]];
    const yCoords = [coords[1], coords[3], coords[5], coords[7]];

    const minX = Math.min(...xCoords);
    const maxX = Math.max(...xCoords);
    const minY = Math.min(...yCoords);
    const maxY = Math.max(...yCoords);

    // Get the canvas inside this page to understand the scale
    const canvas = pageContainer.querySelector('canvas') as HTMLCanvasElement;
    if (!canvas) {
      return;
    }

    // Get actual PDF page dimensions for better precision
    let pdfPageWidth = 612; // Default
    let pdfPageHeight = 792; // Default

    // Try to get actual PDF page dimensions from PDF.js
    if (typeof window !== 'undefined' && (window as any).PDFViewerApplication) {
      const pdfApp = (window as any).PDFViewerApplication;
      if (pdfApp.pdfDocument && pdfApp.pdfViewer) {
        try {
          const targetPageNumber = annotation.placemark?.page_number__sys || 1;
          const pageView = pdfApp.pdfViewer.getPageView(targetPageNumber - 1);
          if (pageView && pageView.pdfPage) {
            const viewport = pageView.pdfPage.getViewport({ scale: 1 });
            pdfPageWidth = viewport.width;
            pdfPageHeight = viewport.height;
          }
        } catch (e) {
          // Use defaults if unable to get dimensions
        }
      }
    }

    // Calculate precise scale factors using actual PDF dimensions
    const scaleX = canvas.offsetWidth / pdfPageWidth;
    const scaleY = canvas.offsetHeight / pdfPageHeight;

    // Convert PDF coordinates (origin bottom-left) to DOM coordinates (origin top-left)
    const scaledLeft = minX * scaleX;
    const scaledTop = (pdfPageHeight - maxY) * scaleY; // Flip Y coordinate
    const scaledWidth = (maxX - minX) * scaleX;
    const scaledHeight = (maxY - minY) * scaleY;

    // Create highlight overlay
    const highlight = document.createElement('div');
    highlight.className = 'page-annotation-highlight';
    highlight.dataset.annotationId = annotation.id__sys;

    highlight.style.cssText = `
      position: absolute;
      left: ${scaledLeft}px;
      top: ${scaledTop}px;
      width: ${scaledWidth}px;
      height: ${scaledHeight}px;
      background-color: rgba(255, 255, 0, 0.7) !important;
      border: 2px solid #FFD700 !important;
      z-index: 999999 !important;
      pointer-events: none;
      border-radius: 3px;
      font-size: 10px;
      color: #333;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      text-shadow: 1px 1px 1px white;
    `;

    // Ensure page container has relative positioning
    const computedStyle = window.getComputedStyle(pageContainer);
    if (computedStyle.position === 'static') {
      pageContainer.style.position = 'relative';
    }

    pageContainer.appendChild(highlight);
    this.highlightElements.push(highlight);

    // Scroll to center the highlighted text
    setTimeout(() => {
      this.scrollToHighlight(pageContainer, highlight);
    }, 100);

    // Auto-remove after 30 seconds
    setTimeout(() => {
      if (highlight.parentNode) {
        highlight.parentNode.removeChild(highlight);
        const index = this.highlightElements.indexOf(highlight);
        if (index > -1) {
          this.highlightElements.splice(index, 1);
        }
      }
    }, 30000);
  }

  private scrollToHighlight(pageContainer: HTMLElement, highlight: HTMLElement): void {
    try {
      // Find the scrollable container (PDF viewer)
      const pdfViewer = document.querySelector('ngx-extended-pdf-viewer');
      if (!pdfViewer) {
        return;
      }

      // Try multiple selectors to find scrollable content
      const scrollableSelectors = [
        '#viewer',
        '.pdfViewer',
        '[class*="scroll"]',
        '.zoom',
        '[class*="viewer"]',
        'div[style*="overflow"]',
        'div[style*="scroll"]',
      ];

      let scrollableElement: HTMLElement | null = null;

      for (const selector of scrollableSelectors) {
        const element = pdfViewer.querySelector(selector) as HTMLElement;
        if (element) {
          const computedStyle = window.getComputedStyle(element);

          // Check if this element can actually scroll
          if (
            element.scrollHeight > element.clientHeight ||
            computedStyle.overflowY === 'scroll' ||
            computedStyle.overflowY === 'auto'
          ) {
            scrollableElement = element;
            break;
          }
        }
      }

      if (!scrollableElement) {
        // Try the PDF viewer itself or its parent
        const candidates = [
          pdfViewer,
          pdfViewer.parentElement,
          document.documentElement,
          document.body,
        ];

        for (const candidate of candidates) {
          if (candidate && candidate instanceof HTMLElement) {
            if (candidate.scrollHeight > candidate.clientHeight) {
              scrollableElement = candidate;
              break;
            }
          }
        }
      }

      if (!scrollableElement) {
        // Fallback: try to scroll the page itself to bring the highlight into view
        const pageRect = pageContainer.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // If the page is not visible in the viewport, scroll to it
        if (pageRect.top < 0 || pageRect.top > viewportHeight) {
          pageContainer.scrollIntoView({
            behavior: 'instant',
            block: 'center',
          });
        }
        return;
      }

      // Calculate the position to scroll to (center the highlight in the viewport)
      const highlightRect = highlight.getBoundingClientRect();
      const viewportHeight = scrollableElement.clientHeight;
      const scrollableRect = scrollableElement.getBoundingClientRect();

      // Calculate the target scroll position
      const highlightCenter = highlightRect.top + highlightRect.height / 2;
      const viewportCenter = scrollableRect.top + viewportHeight / 2;
      const scrollOffset = highlightCenter - viewportCenter;

      const targetScrollTop = scrollableElement.scrollTop + scrollOffset;

      // Instant scroll to the target position
      scrollableElement.scrollTop = Math.max(0, targetScrollTop);
    } catch (error) {
      // Silently fail if scrolling doesn't work
    }
  }

  public clearAllHighlights(): void {
    this.highlightElements.forEach(element => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
    this.highlightElements = [];
  }
}
